// ============================================
// PAGE - Apertura de Caja
// ============================================

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@almacenes/ui';
import { useAbrirCaja, useAuthStore, useUIStore, useVendedoresCaja, getFechaHoyLocal } from '@almacenes/core';
import { MdArrowBack, MdAccountBalanceWallet } from 'react-icons/md';

interface FormData {
  usuarioId: string;
  fechaInicio: string;
  horaInicio: string;
  montoInicial: string;
}

export const AperturaCajaPage = () => {
  const navigate = useNavigate();
  const usuario = useAuthStore((state) => state.usuario);
  const addToast = useUIStore((state) => state.addToast);
  const abrirCajaMutation = useAbrirCaja();
  const { data: vendedores, isLoading: isLoadingVendedores } = useVendedoresCaja();

  const [formData, setFormData] = useState<FormData>({
    usuarioId: usuario?.id || '',
    fechaInicio: getFechaHoyLocal(),
    horaInicio: new Date().toLocaleTimeString('es-CL', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }), // HH:mm:ss en formato 24h
    montoInicial: '',
  });

  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [montoDisplay, setMontoDisplay] = useState('');

  // Función para formatear número con separador de miles (punto)
  const formatearMonto = (valor: string): string => {
    // Remover todo lo que no sea número
    const soloNumeros = valor.replace(/\D/g, '');
    
    if (!soloNumeros) return '';
    
    // Convertir a número y formatear con separador de miles
    const numero = parseInt(soloNumeros, 10);
    return numero.toLocaleString('es-CL');
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    if (field === 'montoInicial') {
      // Para el monto, manejar el formato visual
      const soloNumeros = value.replace(/\D/g, '');
      setFormData((prev) => ({ ...prev, montoInicial: soloNumeros }));
      setMontoDisplay(formatearMonto(soloNumeros));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
    
    // Limpiar error del campo al editar
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};

    // Validar vendedor/usuario
    if (!formData.usuarioId || formData.usuarioId.trim() === '') {
      newErrors.usuarioId = 'Debes seleccionar un vendedor';
    }

    // Validar fecha
    if (!formData.fechaInicio || formData.fechaInicio.trim() === '') {
      newErrors.fechaInicio = 'La fecha de inicio es obligatoria';
    }

    // Validar hora
    if (!formData.horaInicio || formData.horaInicio.trim() === '') {
      newErrors.horaInicio = 'La hora de inicio es obligatoria';
    }

    // Validar monto inicial
    if (!formData.montoInicial || formData.montoInicial.trim() === '') {
      newErrors.montoInicial = 'El monto inicial es obligatorio';
    } else if (isNaN(Number(formData.montoInicial)) || Number(formData.montoInicial) < 0) {
      newErrors.montoInicial = 'El monto debe ser un número válido mayor o igual a 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      await abrirCajaMutation.mutateAsync({
        usuarioId: Number(formData.usuarioId),
        fechaInicio: formData.fechaInicio,
        horaInicio: formData.horaInicio,
        montoInicial: Number(formData.montoInicial),
      });

      addToast('success', 'Caja abierta exitosamente');

      // Navegar de vuelta a la pantalla principal de Caja
      navigate('/caja-360');
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Error al abrir la caja';
      addToast('error', errorMessage);
    }
  };

  return (
    <div className="px-4 md:px-6 lg:px-8 pb-8">
      {/* Header */}
      <div className="mb-6 flex items-center gap-4">
        <button
          onClick={() => navigate('/caja-360')}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <MdArrowBack className="text-2xl" style={{ color: '#43BFFF' }} />
        </button>
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#11194F' }}>
            Apertura de Caja
          </h1>
          <p className="text-gray-600 mt-1">
            Registra el inicio de operaciones de caja
          </p>
        </div>
      </div>

      {/* Formulario */}
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8">
          {/* Icono */}
          <div className="flex justify-center mb-6">
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center"
              style={{ backgroundColor: '#DCFCE7' }}
            >
              <MdAccountBalanceWallet className="text-5xl" style={{ color: '#22C55E' }} />
            </div>
          </div>

          <div className="space-y-6">
            {/* Vendedor - Select */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#11194F' }}>
                Vendedor <span className="text-red-500">*</span>
              </label>
              {isLoadingVendedores ? (
                <div className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50">
                  <span className="text-gray-500">Cargando vendedores...</span>
                </div>
              ) : (
                <select
                  value={formData.usuarioId}
                  onChange={(e) => handleInputChange('usuarioId', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                    errors.usuarioId
                      ? 'border-red-500 focus:ring-red-200'
                      : 'border-gray-300 focus:ring-blue-200'
                  }`}
                  disabled={abrirCajaMutation.isPending || isLoadingVendedores}
                >
                  <option value="">Seleccionar vendedor</option>
                  {vendedores?.map((vendedor) => (
                    <option key={vendedor.id} value={vendedor.id}>
                      {vendedor.nombreMostrable}
                    </option>
                  ))}
                </select>
              )}
              {errors.usuarioId && (
                <p className="text-red-500 text-sm mt-1">{errors.usuarioId}</p>
              )}
            </div>

            {/* Fecha de Inicio */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#11194F' }}>
                Fecha de Inicio <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.fechaInicio}
                onChange={(e) => handleInputChange('fechaInicio', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                  errors.fechaInicio
                    ? 'border-red-500 focus:ring-red-200'
                    : 'border-gray-300 focus:ring-blue-200'
                }`}
                disabled={abrirCajaMutation.isPending}
              />
              {errors.fechaInicio && (
                <p className="text-red-500 text-sm mt-1">{errors.fechaInicio}</p>
              )}
            </div>

            {/* Hora de Inicio */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#11194F' }}>
                Hora de Inicio <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                step="1"
                value={formData.horaInicio}
                onChange={(e) => handleInputChange('horaInicio', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                  errors.horaInicio
                    ? 'border-red-500 focus:ring-red-200'
                    : 'border-gray-300 focus:ring-blue-200'
                }`}
                disabled={abrirCajaMutation.isPending}
              />
              {errors.horaInicio && (
                <p className="text-red-500 text-sm mt-1">{errors.horaInicio}</p>
              )}
            </div>

            {/* Monto Inicial */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#11194F' }}>
                Monto Inicial (CLP) <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
                  $
                </span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={montoDisplay}
                  onChange={(e) => handleInputChange('montoInicial', e.target.value)}
                  placeholder="15.000"
                  className={`w-full pl-8 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                    errors.montoInicial
                      ? 'border-red-500 focus:ring-red-200'
                      : 'border-gray-300 focus:ring-blue-200'
                  }`}
                  disabled={abrirCajaMutation.isPending}
                />
              </div>
              {errors.montoInicial && (
                <p className="text-red-500 text-sm mt-1">{errors.montoInicial}</p>
              )}
              <p className="text-gray-500 text-xs mt-1">
                Ingresa el monto con el que se inicia la caja
              </p>
            </div>

            {/* Botones */}
            <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
              <Button
                variant="secondary"
                onClick={() => navigate('/caja-360')}
                disabled={abrirCajaMutation.isPending}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                variant="success"
                onClick={handleSubmit}
                isLoading={abrirCajaMutation.isPending}
                disabled={abrirCajaMutation.isPending}
                className="flex-1"
              >
                {abrirCajaMutation.isPending ? 'Abriendo caja...' : 'Abrir Caja'}
              </Button>
            </div>
          </div>
        </div>

        {/* Información adicional */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Nota:</strong> La apertura de caja registra el inicio de las operaciones del día. 
            Asegúrate de ingresar el monto inicial correcto para facilitar el cierre de caja.
          </p>
        </div>
      </div>
    </div>
  );
};
